import MagazineComponent from '@pixum/magazine';
import ThemeProvider from '@pixum/theme-provider';
import { RichTextContent, type RichTextDocument } from '../../../richtText';
import { MagazinePicture } from '../../../magazineCollection/variants/partials';
import { Video } from '../../../video/Video';
import { isRichTextEmpty } from '../../../richtText/utils/isRichTextEmpty';
import { THEME_DEFAULT_PROPS } from 'data-layer/constants';
import { getMagazineVariant } from '../../../magazineCollection/utils';
import type { DownloadStepsProps } from '../types';

/**
 * DownloadSteps Component
 *
 * Client-side component that receives the filtered download step article
 * for the user's OS/Browser and renders magazines directly using @pixum/magazine.
 *
 * **Filtering:**
 * - Filtering is handled in DownloadStepsWithSkeleton (parent component)
 * - This component expects only the relevant article as prop
 *
 * **Rendering:**
 * - Uses @pixum/magazine directly instead of MagazineCollection
 * - Builds magazine parts inline (text, media, theme)
 *
 * @param props.article - The platform-specific article (platformKey property)
 * @param props.device - Current device type (mobile/desktop)
 */

export function DownloadSteps({ article, device }: DownloadStepsProps) {
  if (!article) {
    return null;
  }

  if (!article.magazines || article.magazines.length === 0) {
    return null;
  }

  return (
    <>
      {article.magazines.map((magazine, index) => {
        if (Array.isArray(magazine) || 'items' in magazine) {
          return null;
        }

        const text = magazine.text && !isRichTextEmpty(magazine.text as RichTextDocument) && (
          <RichTextContent node={magazine.text} />
        );

        const media = magazine.video ? (
          <Video video={magazine.video} />
        ) : (
          magazine.image && (
            <MagazinePicture image={magazine.image} pictureData={magazine.pictureData} device={device} />
          )
        );

        const magazineTheme = magazine.theme || THEME_DEFAULT_PROPS;
        const { countdown: _countdown, ...magazineProps } = magazine;

        return (
          <ThemeProvider key={magazine.sys.id} theme={magazineTheme}>
            <MagazineComponent
              {...magazineProps}
              contentVariant={getMagazineVariant(index)}
              theme={magazineTheme}
              text={text}
              media={media}
            />
          </ThemeProvider>
        );
      })}
    </>
  );
}
